import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { authService, invitationService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { buildLoginPath, buildRegisterPath, getSafeRedirectPath } from '../utils/redirect';

function InvitationPageNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [autoRejectTriggered, setAutoRejectTriggered] = useState(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');
  const action = searchParams.get('action');
  const currentPath = useMemo(
    () => getSafeRedirectPath(`${location.pathname}${location.search}`, '/'),
    [location.pathname, location.search]
  );

  const loginPath = buildLoginPath(currentPath);
  const registerPath = buildRegisterPath(currentPath, invitation?.invitedEmail);
  const isAuthenticated = Boolean(user || localStorage.getItem('accessToken'));

  useEffect(() => {
    let isMounted = true;

    const loadInvitation = async () => {
      if (!token) {
        if (isMounted) {
          setError(t('El enlace de invitación no es válido.'));
          setLoading(false);
        }
        return;
      }

      try {
        const response = await invitationService.getByToken(token);
        const data = response?.data;
        if (isMounted) setInvitation(data);
      } catch (err) {
        if (isMounted) setError(err?.response?.data?.message || t('No se pudo cargar la invitación'));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInvitation();
    return () => { isMounted = false; };
  }, [t, token]);

  const handleAccept = async () => {
    if (!token) return;
    setProcessing(true);
    setError('');

    try {
      await invitationService.acceptByToken(token);
      // Cuando aceptás una invitación, la membresía cambia → invalidamos
      // también el feed de la home y la actividad reciente para que al
      // volver al dashboard ya aparezcan los productos de la comunidad
      // recién unida sin tener que refrescar a mano.
      queryClient.invalidateQueries({ queryKey: ['userCommunities'] });
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
      queryClient.invalidateQueries({ queryKey: ['productCards'] });
      queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
      setSuccessMessage(t('Invitación aceptada. Redirigiendo a comunidades...'));
      setTimeout(() => navigate('/communities'), 1500);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate(loginPath);
        return;
      }
      setError(err?.response?.data?.message || t('Error al aceptar la invitación'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = useCallback(async () => {
    if (!token) return;
    setProcessing(true);
    setError('');

    try {
      await invitationService.rejectByToken(token);
      setSuccessMessage(t('Invitación rechazada.'));
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate(loginPath);
        return;
      }
      setError(err?.response?.data?.message || t('Error al rechazar la invitación'));
    } finally {
      setProcessing(false);
    }
  }, [navigate, t, token, loginPath]);

  useEffect(() => {
    if (
      action === 'reject'
      && invitation?.status === 'PENDING'
      && isAuthenticated
      && !processing
      && !successMessage
      && !autoRejectTriggered
    ) {
      setAutoRejectTriggered(true);
      handleReject();
    }
  }, [action, autoRejectTriggered, handleReject, invitation?.status, isAuthenticated, processing, successMessage]);

  const handleGoogleLogin = async () => {
    setProcessing(true);
    setError('');

    try {
      const response = await authService.getGoogleAuthUrl(currentPath);
      const payload = response?.data;
      if (!payload?.authUrl) throw new Error(t('No se pudo obtener la URL de autenticación'));
      window.location.href = payload.authUrl;
    } catch (err) {
      setError(err?.message || t('Error al iniciar sesión con Google'));
      setProcessing(false);
    }
  };

  const invitationExpired =
    invitation?.status === 'EXPIRED' ||
    (invitation?.expiresAt && new Date(invitation.expiresAt) < new Date());
  const isPending = invitation?.status === 'PENDING' && !invitationExpired;

  return (
    <div
      className="dashboard-new font-body min-h-[100dvh] flex items-center justify-center px-4"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <div className="w-full max-w-md">
        <div className="bg-surface-container-lowest rounded-xl shadow-lg p-8">
          <h1 className="text-xl font-bold text-center text-on-surface">{t('Invitación a Comunidad')}</h1>

          {loading && (
            <div className="flex flex-col items-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
              <p className="text-on-surface-variant mt-3">{t('Cargando invitación...')}</p>
            </div>
          )}

          {!loading && error && (
            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              <span className="material-symbols-outlined text-[18px]">error</span>
              {error}
            </div>
          )}

          {!loading && successMessage && (
            <div className="mt-4 flex items-center gap-2 p-3 rounded-lg bg-primary-container text-on-primary-container text-sm">
              <span className="material-symbols-outlined text-[18px]">check_circle</span>
              {successMessage}
            </div>
          )}

          {!loading && invitation && !error && (
            <div className="mt-5 space-y-3">
              <h2 className="text-lg font-semibold text-on-surface">{t('Has sido invitado a:')} {invitation.communityName}</h2>
              <p className="text-sm text-on-surface-variant">{t('Invitado por')}: {invitation.inviterName}</p>
              <p className="text-sm text-on-surface-variant">{t('Tu email')}: {invitation.invitedEmail}</p>

              {invitation.message && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-secondary-container text-on-secondary-container text-sm">
                  <span className="material-symbols-outlined text-[18px] mt-0.5">chat</span>
                  <span><strong>{t('Mensaje personal')}:</strong> {invitation.message}</span>
                </div>
              )}

              {invitationExpired && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-error-container/50 text-on-surface text-sm">
                  <span className="material-symbols-outlined text-[18px]">schedule</span>
                  {t('Esta invitación está expirada.')}
                </div>
              )}

              {!invitationExpired && invitation.status !== 'PENDING' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary-container text-on-secondary-container text-sm">
                  <span className="material-symbols-outlined text-[18px]">info</span>
                  {t('Esta invitación ya fue procesada:')} {invitation.status}
                </div>
              )}

              {isPending && !isAuthenticated && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm text-on-surface-variant">{t('Para aceptar esta invitación, inicia sesión o crea una cuenta.')}</p>
                  <button onClick={() => navigate(loginPath)} disabled={processing} className="w-full py-2.5 rounded-lg bg-primary text-on-primary font-medium transition-opacity hover:opacity-90 disabled:opacity-50">
                    {t('Iniciar Sesión')}
                  </button>
                  <button onClick={() => navigate(registerPath)} disabled={processing} className="w-full py-2.5 rounded-lg border-2 border-outline-variant/30 text-on-surface font-medium hover:bg-surface-container-low transition-colors disabled:opacity-50">
                    {t('Registrarse')}
                  </button>
                  <button onClick={handleGoogleLogin} disabled={processing} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-outline-variant/30 text-on-surface font-medium hover:bg-surface-container-low transition-colors disabled:opacity-50">
                    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                    {t('Continuar con Google')}
                  </button>
                </div>
              )}

              {isPending && isAuthenticated && (
                <div className="flex gap-3 pt-2">
                  <button onClick={handleAccept} disabled={processing} className="flex-1 py-2.5 rounded-lg bg-primary text-on-primary font-medium transition-opacity hover:opacity-90 disabled:opacity-50">
                    {t('Aceptar')}
                  </button>
                  <button onClick={handleReject} disabled={processing} className="flex-1 py-2.5 rounded-lg border-2 border-error text-error font-medium hover:bg-error-container transition-colors disabled:opacity-50">
                    {t('Rechazar')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InvitationPageNew;
